import React from 'react';

/** The same operation/recovery explanation is available in every photo view. */
export default function ArrangementPhotoStatus({ workflow }) {
  const {
    busy, uploading, deleting, checking, message, needsCheck, canCheck,
    canRetry, uncertain, checkSavedPhoto, retry, photo, operationKind,
    status, hasSelection, discardSelection,
  } = workflow;
  const progress = uploading ? 'Uploading photo…'
    : deleting ? 'Deleting photo…'
    : checking ? 'Checking saved photo…' : null;

  const canFinish = status === 'checked' || status === 'rejected';
  if (!progress && !message && !needsCheck && !canRetry && !canFinish) return null;

  const handleRetry = () => {
    const displayedPhoto = photo;
    if (uncertain && !window.confirm(
      'The previous request may still complete. Checking shows the current saved photo, but does not prove which request saved it. Retrying may replace or delete the current photo. Retry anyway?',
    )) return;
    if (!uncertain && operationKind === 'delete' && !window.confirm('Delete this arrangement photo?')) return;
    void retry(displayedPhoto);
  };

  const handleFinish = () => {
    if (status === 'checked' && !window.confirm(
      'Use the current saved photo state? An earlier request may still finish and change it. Any selected file retained for retry will be discarded.',
    )) return;
    if (status === 'rejected' && hasSelection && !window.confirm(
      'Discard the selected file and dismiss this failed attempt? You can select another photo afterward.',
    )) return;
    discardSelection();
  };

  return (
    <div className="my-2 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600"
      data-testid="arrangement-photo-status">
      <p role="status" aria-live="polite">{progress || message}</p>
      {progress && message && <p className="mt-1">{message}</p>}
      {(needsCheck || canRetry || canFinish) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {needsCheck && (
            <button type="button" onClick={() => { void checkSavedPhoto(); }}
              disabled={!canCheck || busy}
              className="rounded border border-stone-300 px-2 py-1 font-semibold disabled:opacity-40"
              data-testid="arrangement-photo-check">
              Check saved photo
            </button>
          )}
          {canRetry && (
            <button type="button" onClick={handleRetry} disabled={busy}
              className="rounded border border-stone-300 px-2 py-1 font-semibold disabled:opacity-40"
              data-testid="arrangement-photo-retry">
              Retry
            </button>
          )}
          {canFinish && (
            <button type="button" onClick={handleFinish} disabled={busy}
              className="rounded border border-stone-300 px-2 py-1 font-semibold disabled:opacity-40"
              data-testid="arrangement-photo-finish-recovery">
              {status === 'checked' ? 'Use current photo' : hasSelection ? 'Discard selected file' : 'Dismiss failed attempt'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
